import { useApolloClient, useMutation } from '@apollo/client';
import { Button, Spinner, Tooltip, useRestoreFocusTarget } from '@fluentui/react-components';
import { ErrorCircleRegular } from '@fluentui/react-icons';
import React, { useState } from 'react';
import ExtractedTextPanel from './ExtractedTextPanel';
import { isTextExtractionActive, isTextExtractionFailed } from './textExtractionStatus';
import useToast from '../../../utils/hooks/useToast';
import { GET_COMPANY_MILESTONE_DOCUMENTS } from '../../../utils/statApi/CompaniesApi';
import EXTRACT_DOCUMENT_TEXT from '../../../utils/statApi/TextExtractionApi';

interface IExtractTextButtonProps {
  companyCode: string;
  documentId: number;
  documentTitle: string;
  hasExtractedText?: boolean;
  textExtractionStatus?: string | null;
}

const ExtractTextButton = ({
  companyCode,
  documentId,
  documentTitle,
  hasExtractedText = false,
  textExtractionStatus = null,
}: IExtractTextButtonProps): JSX.Element => {
  const client = useApolloClient();
  const [extract] = useMutation(EXTRACT_DOCUMENT_TEXT);
  // Stays true from click until the list has been refreshed, so the button doesn't flash back to
  // "Extract text" between the mutation finishing and the new status arriving.
  const [submitting, setSubmitting] = useState(false);
  const [panelOpen, setPanelOpen] = useState(false);
  const restoreFocusTargetAttributes = useRestoreFocusTarget();
  const { createToast } = useToast();

  if (hasExtractedText) {
    return (
      <>
        <Button
          {...restoreFocusTargetAttributes}
          aria-label={`View text: ${documentTitle}`}
          onClick={(): void => setPanelOpen(true)}
        >
          View text
        </Button>
        <ExtractedTextPanel
          open={panelOpen}
          companyCode={companyCode}
          documentId={documentId}
          documentTitle={documentTitle}
          onClose={(): void => setPanelOpen(false)}
        />
      </>
    );
  }

  const onClick = async (): Promise<void> => {
    setSubmitting(true);
    try {
      await extract({ variables: { companyCode, documentId } });
    } catch {
      setSubmitting(false);
      createToast({
        id: Math.random(),
        header: 'Text Extraction Error',
        content: 'Unable to queue text extraction. Please try again.',
        iconName: 'ErrorBadge',
      });
      return;
    }

    // The run is already queued; a failed refresh must not be reported as a queueing failure.
    await client
      .refetchQueries({ include: [GET_COMPANY_MILESTONE_DOCUMENTS] })
      .catch((): void => undefined);
    setSubmitting(false);
  };

  if (submitting || isTextExtractionActive(textExtractionStatus)) {
    return (
      <Button
        aria-label={`Extracting text: ${documentTitle}`}
        disabledFocusable
        icon={<Spinner size="tiny" />}
      >
        {textExtractionStatus === 'TEXTEXTRACTIONINPROGRESS' ? 'Extracting text…' : 'Queued…'}
      </Button>
    );
  }

  if (isTextExtractionFailed(textExtractionStatus)) {
    return (
      <Tooltip content="Text extraction failed. Select to retry." relationship="description">
        <Button
          aria-label={`Retry text extraction: ${documentTitle}`}
          icon={<ErrorCircleRegular />}
          onClick={onClick}
        >
          Retry
        </Button>
      </Tooltip>
    );
  }

  return (
    <Button aria-label={`Extract text: ${documentTitle}`} onClick={onClick}>
      Extract text
    </Button>
  );
};

export default ExtractTextButton;
